import {
  createSerializer,
  inferParserType,
  parseAsArrayOf,
  parseAsString,
} from "nuqs";

/**
 * Defines the parsers for the search parameters used across all Fabric 3 applications.
 * These parsers are used to parse and validate the query parameters from the URL, ensuring they conform to expected types and formats.
 *
 * 🚨 Declare in this file only the parsers that are going to be shared across multiple applications. For application-specific parsers,
 * please declare them in the respective application's service file. Example: apps/data/src/services/searchParams.service.ts
 * and them merge them with the coreSearchParamsParsers when using them in the useFabricQueryStates hook.
 *
 * To use these parsers in a component, you can use the `useQueryStates` hook from `nuqs` package
 * It works similar to a useState() hook, returning a tuple where the first position contains the values, and the second
 * is a setter function to set the parameters:
 * ```ts
 * const [{ param1, param2, ... }, setSearchParams] = useQueryStates({ ... });
 * ```
 * More info: https://nuqs.dev/docs/basic-usage
 *
 * @example
 * Let's assume we have the following URL:
 * `http://localhost?operationIds=op1,op2&displayHello=true`
 *
 * // Your app specific parsers located in apps/myApp/src/services/searchParams.service.ts
 * ```ts
 * export const myAppSearchParamsParsers = {
 *   ...coreSearchParamsParsers, // don't forget to spread the core parsers to have them available in your app
 *   // app specific parsers
 *   displayHello: parseAsBoolean().withDefault(false),
 *   pages: parseAsArrayOf(parseAsNumber),
 * };
 *
 * // Export a stringify function based on your app specific parsers
 * export const stringifyQueryParams = createSerializer(analysisSearchParamsParsers);
 * ```
 *
 * ```ts
 * import { useFabricQueryStates } from "@granular/fabric3-core";
 *
 * const MyComponent = () => {
 *   // remember to import only the parsers you need, this avoids unnecessary re-renders when other parameters change
 *   const [
 *     {
 *       operationIds,
 *       displayHello,
 *       pages,
 *     },
 *   ] = useFabricQueryStates({
 *     operationIds: myAppSearchParamsParsers.operationIds,
 *     displayHello: myAppSearchParamsParsers.displayHello,
 *     pages: myAppSearchParamsParsers.pages,
 *   });
 *
 *    console.log(operationIds); // string[]
 *    // ["op1", "op2"]
 *
 *    console.log(displayHello); // boolean
 *    // "bc1"
 *
 *    console.log(pages); // number[] | null
 *    // null (not present in the URL and no default value specified)
 * }
 * ```
 */
export const coreSearchParamsParsers = {
  filteredBusinessId: parseAsString.withOptions({ history: "replace" }),
  operationIds: parseAsArrayOf(parseAsString).withDefault([]).withOptions({
    history: "replace",
  }),
  commonBusinessCycleId: parseAsString,
  cropSeasonIds: parseAsArrayOf(parseAsString).withDefault([]),
  // Other examples:

  // String Literal example:
  // When known values are expected don't use parseAsString, use `parseAsStringLiteral` and pass the expected values
  // If the value passed in the query params is not one of the expected ones, the parser will return null
  // or the default value if specified.
  // sortDirection: parseAsStringLiteral(["asc", "desc"]).withDefault("asc")),

  // Boolean example:
  // Boolean is a good case to use the default value. If the parameter is not present, it will return the default value.
  // displayHello: parseAsBoolean().withDefault(false),

  // Number[] example:
  // In this example we're not specifying a default value, so if the parameter is not present, it will return null.
  // pages: parseAsArrayOf(parseAsNumber);
};

/**
 * Converts an object to a query string URL format
 * The function uses the searchParamsParsers to ensure the correct encoding of the parameters.
 * ⚠️ Note that the initial `?` will be added automatically to the beginning of the string.
 * 🚨 Don't export stringifyQueryParams for consumers. They must create their stringifyQueryParams method based on
 * the parsers they are using in their application. It's exported here for internal use in the core package only.
 *
 * @example
 * ```ts
 * const stringifiedParams = stringifyQueryParams({
 *   operationIds: ['op1', 'op2'],
 *   commonBusinessCycleId: 'bc1',
 *   commodityIds: null // null values are not rendered and will be removed from the query string
 * });
 *
 * // Will result in:
 * // ?operationIds=op1,op2&commonBusinessCycleId=bc1
 *
 * // Then use the setSearchParams function from useFabricQueryStates to update the URL
 * setSearchParams(stringifiedParams);
 * ```
 */
export const stringifyQueryParams = createSerializer(coreSearchParamsParsers);

export type CoreSearchParams = inferParserType<typeof coreSearchParamsParsers>;

/**
 * This is just a re-export to have a consistent naming convention across Fabric 3 applications.
 */
export {
  useQueryStates as useFabricQueryStates,
  useQueryState as useFabricQueryState,
} from "nuqs";
